import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { ShoppingCart, Menu, X, Wifi, Truck } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const Header = () => {
  const { lang, setLang, t, langs } = useLang();
  const { totalQty } = useCart();
  const current = langs.find(l => l.code === lang);

  const links = [
    { to: "/", label: t.nav.home, id: "nav-home" },
    { to: "/boutique", label: t.nav.shop, id: "nav-shop" },
    { to: "/fonctionnement", label: t.nav.how, id: "nav-how" },
    { to: "/faq", label: t.nav.faq, id: "nav-faq" },
    { to: "/a-propos", label: t.nav.about, id: "nav-about" },
    { to: "/contact", label: t.nav.contact, id: "nav-contact" },
    { to: "/suivi", label: t.nav.track, id: "nav-track" },
  ];

  return (
    <header className="glass sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-bold text-lg" data-testid="brand-link" aria-label="NFC Avis Pro — Accueil">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#2563EB] text-white" aria-hidden="true">
            <Wifi className="w-5 h-5 rotate-90" />
          </span>
          <span className="brand-gradient-text tracking-tight">NFC Avis Pro</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={l.id}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-medium ${isActive ? "text-blue-400 bg-slate-900" : "text-slate-200 hover:text-blue-400 hover:bg-[#0a0a0a]"}`
              }>{l.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full" data-testid="lang-switcher" aria-label={{"fr":"Changer de langue","en":"Change language","es":"Cambiar idioma","zh":"更改语言"}[lang]}>
                <span className="mr-1" aria-hidden="true">{current?.flag}</span> {current?.code.toUpperCase()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {langs.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}>
                  <span className="mr-2">{l.flag}</span> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Cart + free-shipping progress */}
          <div className="flex items-center gap-1.5" data-testid="cart-block">
            {totalQty > 0 && totalQty < 2 && (
              <Link to="/panier" className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-semibold hover:bg-blue-500/20" data-testid="cart-free-shipping-progress">
                <Truck className="w-3 h-3" />
                <span>{{"fr":"+1 carte = livraison offerte","en":"+1 card = free shipping","es":"+1 tarjeta = envío gratis","zh":"再加 1 张 = 免运费"}[lang]}</span>
              </Link>
            )}
            {totalQty >= 2 && (
              <Link to="/panier" className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/20" data-testid="cart-free-shipping-unlocked">
                <Truck className="w-3 h-3" />
                <span>{{"fr":"Livraison offerte ✓","en":"Free shipping ✓","es":"Envío gratis ✓","zh":"免运费 ✓"}[lang]}</span>
              </Link>
            )}

            <Link to="/panier" className="relative inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-slate-800" data-testid="cart-link" aria-label={{"fr":`Panier${totalQty>0?` (${totalQty})`:""}`,"en":`Cart${totalQty>0?` (${totalQty})`:""}`,"es":`Carrito${totalQty>0?` (${totalQty})`:""}`,"zh":`购物车${totalQty>0?` (${totalQty})`:""}`}[lang]}>
              <ShoppingCart className="w-5 h-5" aria-hidden="true" />
              {totalQty > 0 && (
                <span className={`absolute -top-1 -right-1 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold ${totalQty >= 2 ? "bg-emerald-500" : "bg-[#2563EB]"}`} data-testid="cart-badge" aria-hidden="true">{totalQty}</span>
              )}
            </Link>
          </div>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn" aria-label={{"fr":"Ouvrir le menu","en":"Open menu","es":"Abrir menú","zh":"打开菜单"}[lang]}>
                <Menu className="w-5 h-5" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <div className="mt-8 flex flex-col gap-2" data-testid="mobile-nav">
                {links.map((l) => (
                  <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`m-${l.id}`}
                    className={({ isActive }) => `px-4 py-3 rounded-lg font-medium ${isActive ? "text-blue-400 bg-slate-900" : "text-slate-200 hover:bg-[#0a0a0a]"}`}>
                    {l.label}
                  </NavLink>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
